/**
 * Leaderboard Configuration
 * Central configuration for all games that appear in the leaderboard
 */

export const GAMES = [
  {
    id: 'wordle',
    name: 'HUdle',
    iconName: 'grid', // Lucide icon name
    description: 'Daily word puzzle',
    enabled: true,
    path: '/wordle',
    timeFilters: ['today', 'week', 'alltime'],
    alltimeViews: ['players', 'scores'],
    // Column configurations for each view
    columns: {
      today: [
        { key: 'rank', label: 'Rank', width: '80px' },
        { key: 'username', label: 'Player', width: '25%' },
        { key: 'attempts', label: 'Guesses', width: '15%', clickable: true },
        { key: 'time', label: 'Time', width: '12%' },
        { key: 'word', label: 'Word', width: '12%' }
      ],
      week: [
        { key: 'rank', label: 'Rank', width: '80px' },
        { key: 'username', label: 'Player', width: '20%' },
        { key: 'attempts', label: 'Guesses', width: '12%', clickable: true },
        { key: 'time', label: 'Time', width: '12%' },
        { key: 'word', label: 'Word', width: '12%' },
        { key: 'date', label: 'Date', width: '15%' }
      ],
      alltime_players: [
        { key: 'rank', label: 'Rank', width: '80px' },
        { key: 'username', label: 'Player', width: '25%' },
        { key: 'avgGuesses', label: 'Avg Guesses', width: '15%' },
        { key: 'gamesWon', label: 'Games Won', width: '15%' },
        { key: 'winRate', label: 'Win Rate', width: '15%' }
      ],
      alltime_scores: [
        { key: 'rank', label: 'Rank', width: '80px' },
        { key: 'username', label: 'Player', width: '20%' },
        { key: 'attempts', label: 'Guesses', width: '12%', clickable: true },
        { key: 'time', label: 'Time', width: '12%' },
        { key: 'word', label: 'Word', width: '12%' },
        { key: 'date', label: 'Date', width: '15%' }
      ]
    },
    // Ranking criteria
    primaryMetric: 'attempts',
    secondaryMetric: 'completionTimeMs',
    sortOrder: 'asc', // Lower is better
    // Footer messages for each view
    footerMessages: {
      today: [
        "Today's daily word • One appearance per player",
        "Rankings: 1) Fewest guesses, 2) Fastest time (tiebreaker) • Click on guesses to see details"
      ],
      week: [
        "This week's leaderboard (Monday-Sunday) • One appearance per player",
        "Rankings: 1) Fewest guesses, 2) Fastest time (tiebreaker)"
      ],
      alltime_players: [
        "Rankings based on average guesses (lower is better)",
        "Minimum 1 game required to appear"
      ],
      alltime_scores: [
        "Best individual game performances • Players can appear multiple times",
        "Rankings: 1) Fewest guesses, 2) Fastest time (tiebreaker) • Click on guesses to see details"
      ]
    }
  },
  {
    id: 'campusGuesser',
    name: 'Campus Guesser',
    iconName: 'mapPin', // Lucide icon name
    description: 'Guess the HU location',
    enabled: true,
    path: '/campus-guesser',
    timeFilters: ['today', 'week', 'alltime'],
    alltimeViews: ['players', 'scores'],
    columns: {
      today: [
        { key: 'rank', label: 'Rank', width: '80px' },
        { key: 'username', label: 'Player', width: '25%' },
        { key: 'distance', label: 'Distance', width: '15%' },
        { key: 'score', label: 'Score', width: '15%' },
        { key: 'time', label: 'Time', width: '12%' }
      ],
      week: [
        { key: 'rank', label: 'Rank', width: '80px' },
        { key: 'username', label: 'Player', width: '20%' },
        { key: 'distance', label: 'Distance', width: '12%' },
        { key: 'score', label: 'Score', width: '12%' },
        { key: 'time', label: 'Time', width: '12%' },
        { key: 'date', label: 'Date', width: '15%' }
      ],
      alltime_players: [
        { key: 'rank', label: 'Rank', width: '80px' },
        { key: 'username', label: 'Player', width: '25%' },
        { key: 'avgDistance', label: 'Avg Distance', width: '15%' },
        { key: 'avgScore', label: 'Avg Score', width: '15%' },
        { key: 'gamesPlayed', label: 'Games', width: '15%' }
      ],
      alltime_scores: [
        { key: 'rank', label: 'Rank', width: '80px' },
        { key: 'username', label: 'Player', width: '20%' },
        { key: 'distance', label: 'Distance', width: '12%' },
        { key: 'score', label: 'Score', width: '12%' },
        { key: 'time', label: 'Time', width: '12%' },
        { key: 'date', label: 'Date', width: '15%' }
      ]
    },
    primaryMetric: 'distance',
    secondaryMetric: 'completionTimeMs',
    sortOrder: 'asc', // Lower distance is better
    footerMessages: {
      today: [
        "Today's location challenge • One appearance per player",
        "Rankings: 1) Closest distance, 2) Fastest time (tiebreaker)"
      ],
      week: [
        "This week's leaderboard (Monday-Sunday) • One appearance per player",
        "Rankings: 1) Closest distance, 2) Fastest time (tiebreaker)"
      ],
      alltime_players: [
        "Rankings based on average distance (closer is better)",
        "Minimum 1 game required to appear"
      ],
      alltime_scores: [
        "Best individual game performances • Players can appear multiple times",
        "Rankings: 1) Closest distance, 2) Fastest time (tiebreaker)"
      ]
    }
  }
];

/**
 * Get a game configuration by ID
 * @param {string} gameId - The game ID
 * @returns {Object|null} - Game config or null if not found
 */
export function getGameConfig(gameId) {
  return GAMES.find(game => game.id === gameId) || null;
}

/**
 * Get all enabled games
 * @returns {Array} - Array of enabled game configs
 */
export function getEnabledGames() {
  return GAMES.filter(game => game.enabled);
}

/**
 * Get the default game ID
 * @returns {string} - The first enabled game's ID
 */
export function getDefaultGameId() {
  const enabledGames = getEnabledGames();
  return enabledGames.length > 0 ? enabledGames[0].id : 'wordle';
}

